import { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axios';
import { SERVER } from '../../../hooks/config';
import { MdImage, MdDelete, MdVisibility } from 'react-icons/md';
import './ImageManager.scss';

const HomeImages = () => {
  const [images, setImages] = useState({
    banner: null,
    perso: [],
    testimonials: []
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/home/getImages');
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
      setError('Failed to fetch images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (type, file) => {
    if (!file) {
      setError('Please select an image file first');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setMessage('');

      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', type);

      const response = await axiosInstance.post('/home/uploadImage', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setMessage('Image uploaded successfully!');
      fetchImages(); // Refresh images
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(error.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imagePath, type, index = null) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.delete('/home/deleteImage', {
        data: { imagePath }
      });
      
      setMessage('Image deleted successfully!');
      fetchImages(); // Refresh images
    } catch (error) {
      console.error('Error deleting image:', error);
      setError(error.response?.data?.error || 'Failed to delete image');
    } finally {
      setLoading(false);
    }
  };

  const ImageUploadSection = ({ title, description, type, currentImages, multiple = false }) => (
    <div className="image-section">
      <h3>{title}</h3>
      <p>{description}</p>
      
      <div className="file-input-container">
        <input
          type="file"
          id={`${type}-upload`}
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) handleFileUpload(type, file);
            e.target.value = ''; // Reset input
          }}
          className="file-input"
          multiple={multiple}
        />
        <label htmlFor={`${type}-upload`} className="file-input-label">
          <MdImage />
          Upload {multiple ? 'Images' : 'Image'}
        </label>
      </div>

      <div className="current-images">
        {multiple ? (
          <div className="image-grid">
            {currentImages.map((image, index) => (
              <div key={index} className="image-item">
                <img src={`${SERVER}${image}`} alt={`${type} ${index + 1}`} />
                <div className="image-actions">
                  <a href={`${SERVER}${image}`} target="_blank" rel="noopener noreferrer">
                    <MdVisibility />
                  </a>
                  <button onClick={() => handleDelete(image, type, index)}>
                    <MdDelete />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          currentImages && (
            <div className="single-image">
              <img src={`${SERVER}${currentImages}`} alt={type} />
              <div className="image-actions">
                <a href={`${SERVER}${currentImages}`} target="_blank" rel="noopener noreferrer">
                  <MdVisibility />
                </a>
                <button onClick={() => handleDelete(currentImages, type)}>
                  <MdDelete />
                </button>
              </div>
            </div>
          )
        )}
        {(!currentImages || (multiple && currentImages.length === 0)) && (
          <p className="no-images">No images uploaded yet</p>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="image-manager-loading">
        <div className="loading-spinner"></div>
        <p>Loading images...</p>
      </div>
    );
  }

  return (
    <div className="image-manager">
      <div className="manager-header">
        <h2>Home Page Images Management</h2>
        <p>Upload and manage images for the home page</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {message && (
        <div className="success-message">
          {message}
        </div>
      )}

      <ImageUploadSection
        title="Banner Image"
        description="Main banner image displayed at the top of the home page"
        type="banner"
        currentImages={images.banner}
      />

      <ImageUploadSection
        title="Personalization Examples"
        description="Images showing personalized product examples (upload 2 images)"
        type="perso"
        currentImages={images.perso}
        multiple={true}
      />

      <ImageUploadSection
        title="Testimonials"
        description="Customer testimonial images (upload 2 images)"
        type="testimonial"
        currentImages={images.testimonials}
        multiple={true}
      />
    </div>
  );
};

export default HomeImages;